import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

// One point per ET day for a single line (urgent or regular). `value` is null
// for empty days so the line breaks into a gap rather than dropping to zero.
export type TvDailyPoint = {
    readonly day: string;
    readonly value: number | null;
    readonly runCount: number;
};

export type TvDailySeries = {
    readonly label: string;
    readonly color: string;
    readonly points: Array<TvDailyPoint>;
    readonly countLabel?: string;
};

type TvDailyLineChartProps = {
    readonly urgent: TvDailySeries;
    readonly regular: TvDailySeries;
    readonly additional?: TvDailySeries;
    readonly valueFormatter: (value: number) => string;
    readonly height?: number;
    // Fixed y-domain override (e.g. [0, 1] for the 0–1 quality fraction).
    readonly yDomain?: [number, number];
};

type SeriesMeta = {
    readonly valueKey: 'urgent' | 'regular' | 'additional';
    readonly runsKey: 'urgentRuns' | 'regularRuns' | 'additionalRuns';
    readonly label: string;
    readonly color: string;
    readonly countLabel: string;
};

type DailyChartRow = {
    readonly day: string;
    readonly urgent: number | null;
    readonly regular: number | null;
    readonly additional: number | null;
    readonly urgentRuns: number;
    readonly regularRuns: number;
    readonly additionalRuns: number;
};

// Plain ISO date (YYYY-MM-DD) → `M/d`. Parse the parts directly so the label
// reflects the ET day the API keyed on, with no timezone math.
function formatDayTick(day: string): string {
    const [, month, dayOfMonth] = day.split('-');
    return `${Number(month)}/${Number(dayOfMonth)}`;
}

function TvDailyTooltip({
    active,
    payload,
    label,
    series,
    valueFormatter,
}: {
    readonly active?: boolean;
    readonly payload?: Array<{ readonly payload?: DailyChartRow }>;
    readonly label?: string;
    readonly series: Array<SeriesMeta>;
    readonly valueFormatter: (value: number) => string;
}) {
    if (!active || payload === undefined || payload.length === 0) {
        return null;
    }
    const row = payload[0]?.payload;
    return (
        <div className="bg-background rounded-md border px-3 py-2 text-xs shadow-sm">
            <div className="mb-1 font-medium">
                {label === undefined ? '' : formatDayTick(label)}
            </div>
            {series.map((entry) => {
                const value = row?.[entry.valueKey] ?? null;
                const runs = row?.[entry.runsKey] ?? 0;
                return (
                    <div
                        key={entry.valueKey}
                        className="flex items-center justify-between gap-4"
                    >
                        <span className="flex items-center gap-1.5">
                            <span
                                className="inline-block h-0 w-3 border-t-2"
                                style={{ borderColor: entry.color }}
                            />
                            <span className="text-muted-foreground">
                                {entry.label}
                            </span>
                        </span>
                        <span className="tabular-nums">
                            {value === null ? '—' : valueFormatter(value)}
                            <span className="text-muted-foreground ml-2">
                                {runs} {entry.countLabel}
                            </span>
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

export function TvDailyLineChart({
    urgent,
    regular,
    additional,
    valueFormatter,
    height = 240,
    yDomain,
}: TvDailyLineChartProps) {
    const series: Array<SeriesMeta> = [
        {
            valueKey: 'urgent',
            runsKey: 'urgentRuns',
            label: urgent.label,
            color: urgent.color,
            countLabel: urgent.countLabel ?? 'runs',
        },
        {
            valueKey: 'regular',
            runsKey: 'regularRuns',
            label: regular.label,
            color: regular.color,
            countLabel: regular.countLabel ?? 'runs',
        },
    ];
    if (additional !== undefined) {
        series.push({
            valueKey: 'additional',
            runsKey: 'additionalRuns',
            label: additional.label,
            color: additional.color,
            countLabel: additional.countLabel ?? 'runs',
        });
    }
    const urgentByDay = new Map(
        urgent.points.map((point) => [point.day, point]),
    );
    const regularByDay = new Map(
        regular.points.map((point) => [point.day, point]),
    );
    const additionalByDay = new Map(
        additional?.points.map((point) => [point.day, point]) ?? [],
    );
    const days = Array.from(
        new Set([
            ...urgentByDay.keys(),
            ...regularByDay.keys(),
            ...additionalByDay.keys(),
        ]),
    ).sort();
    const rows: Array<DailyChartRow> = days.map((day) => {
        const urgentPoint = urgentByDay.get(day);
        const regularPoint = regularByDay.get(day);
        const additionalPoint = additionalByDay.get(day);
        return {
            day,
            urgent: urgentPoint?.value ?? null,
            regular: regularPoint?.value ?? null,
            additional: additionalPoint?.value ?? null,
            urgentRuns: urgentPoint?.runCount ?? 0,
            regularRuns: regularPoint?.runCount ?? 0,
            additionalRuns: additionalPoint?.runCount ?? 0,
        };
    });
    // Recharts renders no y ticks when every point is null, even with an
    // explicit domain; derive fixed ticks so the axis survives empty data.
    const yTicks = yDomain
        ? Array.from(
              { length: 5 },
              (_, index) =>
                  yDomain[0] + ((yDomain[1] - yDomain[0]) * index) / 4,
          )
        : undefined;
    const hasAnyValue = rows.some(
        (row) =>
            row.urgent !== null ||
            row.regular !== null ||
            row.additional !== null,
    );
    if (!hasAnyValue) {
        return (
            <div
                className="text-muted-foreground flex items-center justify-center text-sm"
                style={{ height }}
            >
                No data in the last 14 days
            </div>
        );
    }
    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3 text-xs">
                {series.map((entry) => (
                    <span
                        key={entry.valueKey}
                        className="flex items-center gap-1.5"
                    >
                        <span
                            className="inline-block h-0 w-4 border-t-2"
                            style={{ borderColor: entry.color }}
                        />
                        <span className="text-muted-foreground">
                            {entry.label}
                        </span>
                    </span>
                ))}
            </div>
            <div className="w-full" style={{ height }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={rows} margin={{ left: 8, right: 8 }}>
                        <CartesianGrid
                            stroke="var(--border)"
                            vertical={false}
                        />
                        <XAxis
                            dataKey="day"
                            tick={{ fontSize: 11 }}
                            tickFormatter={formatDayTick}
                        />
                        <YAxis
                            tick={{ fontSize: 11 }}
                            width={48}
                            domain={yDomain}
                            ticks={yTicks}
                            tickFormatter={valueFormatter}
                        />
                        <RechartsTooltip
                            content={
                                <TvDailyTooltip
                                    series={series}
                                    valueFormatter={valueFormatter}
                                />
                            }
                        />
                        {series.map((entry) => (
                            <Line
                                key={entry.valueKey}
                                type="monotone"
                                dataKey={entry.valueKey}
                                name={entry.label}
                                stroke={entry.color}
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                connectNulls={false}
                                isAnimationActive={false}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
